<script setup lang="ts">
import { ref, inject, getCurrentInstance, onMounted } from "vue";
import { TranslationService } from "../services/translation.service";
import { CartService } from "../services/cart.service";
import { OverlaysService } from "../services/overlays.service";
import { EOverlay } from "../EOverlay";
import { ItemService } from "../services/item.service";
import { ProductsService } from "../services/products.service";
import { vLoad } from "../directives/v-load";
import { IAddressBlock } from "../interfaces/cart.interfaces";

import Checkout from "./checkout.vue";
import CartOverview from "./cart-overview.vue";
import { BASEURL } from "@libTs/cms.globals";

const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;

const cartSvc = inject<CartService>("cartSvc") as CartService;
const overlaysSvc = inject<OverlaysService>("overlaysSvc") as OverlaysService;
const itemSvc = inject<ItemService>("itemSvc") as ItemService;
const productsSvc = inject<ProductsService>("productsSvc") as ProductsService;

const {
    isOci,
    homeUrl,
    priceDisclaimer,
    billingAddresses,
    shippingAddresses,
    webPermission,
} = defineProps({
    isOci: {
        type: Boolean,
        required: true,
    },
    homeUrl: {
        type: String,
        required: true,
    },
    priceDisclaimer: {
        type: String,
        required: true,
    },
    billingAddresses: {
        type: Array<IAddressBlock>,
        required: true,
    },
    shippingAddresses: {
        type: Array<IAddressBlock>,
        required: true,
    },
    webPermission: {
        type: Number,
        required: true,
    },
});

const THP_ITEMCODE = "thp";
const THP_ITEMNAME = "Third-party";
const baseUrl = BASEURL;

const isLoading = ref(cartSvc.isLoading);
const cartObj = ref(cartSvc.cartObj);
const hasCartInitError = ref(cartSvc.hasCartInitError);

const step = ref(1); // not used currently

// onmounted
onMounted(() => {
    // listen to event
    document.addEventListener("cartLoaded", forceUpdate);
    // console.log("Cart mounted, priceDisclaimer:", priceDisclaimer);
});

const forceUpdate = () => {
    isLoading.value = cartSvc.isLoading;
    cartObj.value = cartSvc.cartObj;
    hasCartInitError.value = cartSvc.hasCartInitError;

    // Force update
    // const instance = getCurrentInstance();
    // instance?.proxy?.$forceUpdate();
};

const submitOciCart = () => {
    const form = document.getElementById(
        "order-form",
    ) as HTMLFormElement | null;
    const formDataInput = document.getElementById(
        "order-form-data",
    ) as HTMLInputElement | null;
    if (!form || !formDataInput) {
        alert(
            "Submission form for OCI not found.\nPlease report this error to\nsales@brunschwig-ch.com",
        );
        return;
    }

    const orderObj = cartSvc.getOrderRequestObjectOci();
    if (Object.keys(orderObj).length === 0) {
        // console.log("Default cart is empty.");
        return;
    }

    formDataInput.value = JSON.stringify(orderObj);
    // console.log(formDataInput.value);

    // Ask for final confirmation
    // if ( !confirm( 'Do you want to send this basket?' ) ) {
    //   return;
    // }

    // Clear default cart
    cartSvc.clearCart(cartSvc.cartObj.carts[0], true);

    form.submit();
};

const showCheckout = () => {
    // console.log("showCheckout");
    step.value = 2;
    window.scrollTo(0, 0);

    // overlaysSvc.showOverlay(EOverlay.shipping);
};

const cartHasItems = () => {
    let returnValue = false;

    cartObj.value.carts.forEach((cart) => {
        if (cart.items.length > 0) {
            returnValue = true;
        }
    });

    return returnValue;
};
</script>

<template>
    <div class="" v-if="step === 1">
        <div class="max-w-7xl mx-auto">
            <div class="row">
                <!-- Cart loading... -->
                <div v-if="isLoading" class="loading-animation"></div>

                <!-- Error initializing cart -->
                <div class="col-lg-offset-1 col-lg-11" v-if="hasCartInitError">
                    <p style="color: red">
                        There was an error loading your cart. Please refresh
                        this page to try again. If the error persists, please
                        contact us via email
                        <a href="mailto:info@brunschwig-ch.com"
                            >info@brunschwig-ch.com</a
                        >.
                    </p>
                </div>

                <!--  Cart, after being initialized -->
                <div class="col-lg-offset-1 col-lg-11" v-if="!hasCartInitError">
                    <div v-if="cartObj && cartObj.isOrderForOtherUser">
                        <p style="color: red">
                            This cart is prepared for the customer with the
                            email "<span v-text="cartObj.customerEmail"></span>
                            <span v-if="cartObj.customerNumber"
                                >({{ cartObj.customerNumber }})</span
                            >".<br />
                        </p>
                    </div>

                    <div class="row">
                        <div class="col-xs-12">
                            <div class="grid-flex">
                                <div
                                    class="flex-vcenter col-m-24 col-l-6 h2 nopadding-mobile"
                                >
                                    <span>Cart</span>
                                </div>
                                <!--<div
                                 class="flex-vcenter col-xl-14 hide-xs hide-s hide-m">
                                {{ t("CHECK_ORDER") }}
                            </div>
                            <div
                                 class="flex-vcenter col-xl-4 hide-xs hide-s hide-m text-right">
                                <a
                                   class="oldbtn-cb"
                                   style="vertical-align: middle"
                                   :href="homeUrl">{{ t("PRODUCTS") }}</a>
                            </div>-->
                            </div>
                        </div>
                    </div>

                    <div>
                        <cart-overview
                            :price-disclaimer="priceDisclaimer"
                        ></cart-overview>

                        <div class="" v-if="!cartHasItems()">
                            <span>{{ t("EMPTY_BUCKET") }}</span>
                        </div>

                        <!--  Webshop OCI: Order form (for cart sumbission to SRM) -->
                        <form
                            v-if="isOci"
                            :action="BASEURL + 'oci/basket/order'"
                            method="POST"
                            id="order-form"
                        >
                            <input
                                type="hidden"
                                id="order-form-data"
                                name="order"
                                value=""
                            />
                        </form>
                    </div>
                    <div
                        class="pb-12 w-full flex justify-end"
                        v-if="cartHasItems()"
                    >
                        <button
                            v-if="isOci"
                            class="btn-cb mt-12"
                            @click="submitOciCart()"
                            type="button"
                        >
                            Submit cart
                            <i class="fas fa-arrow-right"></i>
                        </button>

                        <button
                            v-else
                            class="btn-cb mt-12"
                            @click="showCheckout()"
                            type="button"
                        >
                            Order <i class="fas fa-arrow-right"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="" v-if="step === 2">
        <checkout
            :billing-addresses="billingAddresses"
            :shipping-addresses="shippingAddresses"
            :web-permission="webPermission"
            :price-disclaimer="priceDisclaimer"
        ></checkout>
    </div>
</template>
